import { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { useEnsuredForwardedRef } from 'react-use';

function useDeviceChartMarker({ onCreate, onUpdate, onDelete, onMarkerPlaceEnd }, forwardedRef) {
  const ref = useEnsuredForwardedRef(forwardedRef);
  const timeoutRef = useRef(null);
  const [popup, setPopup] = useState(initPopup);
  const [dialog, setDialog] = useState(initDialog);
  const cancelPopup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
  const enablePopup = useCallback((state) => ({ ...state, enabled: true }), []);
  const disablePopup = useCallback((state) => ({ ...state, enabled: false }), []);
  const showPopup = useCallback(
    (state, markers) =>
      state.enabled
        ? { enabled: true, open: true, markers, virtualRef: createVirtualRef(ref, markers[0]?.element) }
        : state,
    [ref]
  );
  const hidePopup = useCallback((state) => (state.enabled ? initPopup() : state), []);
  const handleHover = useCallback(
    (markers) => {
      cancelPopup();

      if (markers.length > 0) {
        setPopup((state) => showPopup(state, markers));
      } else {
        timeoutRef.current = setTimeout(() => {
          setPopup(hidePopup);
        }, 500);
      }
    },
    [cancelPopup, showPopup, hidePopup]
  );
  const handleEdit = useCallback((marker) => {
    setDialog({ open: true, edit: true, marker });
  }, []);
  const handleClose = useCallback(() => {
    setDialog(initDialog);
  }, []);
  const handleCreate = useCallback(
    (marker) => {
      handleClose();
      onCreate({ name: marker.label, time: marker.value });
    },
    [handleClose, onCreate]
  );
  const handleUpdate = useCallback(
    (marker) => {
      handleClose();
      onUpdate({ annotationId: marker.id, name: marker.label, time: marker.value });
    },
    [handleClose, onUpdate]
  );
  const handleDelete = useCallback(
    (marker) => {
      onDelete({ annotationId: marker.id });
      setPopup(enablePopup);
      setPopup(hidePopup);
    },
    [onDelete, enablePopup, hidePopup]
  );

  const handleCancel = useCallback(
    (marker) => {
      if (ref.current) {
        ref.current.removeMarker(marker);
      }

      handleClose();
    },
    [ref, handleClose]
  );
  const handlePlace = useCallback(
    (marker, event) => {
      setDialog({ open: true, edit: false, marker });
      onMarkerPlaceEnd(marker, event);
    },
    [onMarkerPlaceEnd]
  );
  const handleMouseEnter = useCallback(() => {
    cancelPopup();
    setPopup(disablePopup);
  }, [cancelPopup, disablePopup]);
  const handleMouseLeave = useCallback(() => {
    setPopup(enablePopup);
    timeoutRef.current = setTimeout(() => {
      setPopup(hidePopup);
    }, 500);
  }, [enablePopup, hidePopup]);

  useEffect(() => cancelPopup, [cancelPopup]);

  return {
    ref,
    onMarkerHover: handleHover,
    onMarkerDragEnd: handleUpdate,
    onMarkerPlaceEnd: handlePlace,
    popup: {
      open: popup.open,
      virtualRef: popup.virtualRef,
      markers: popup.markers,
      onEdit: handleEdit,
      onDelete: handleDelete,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    },
    dialog: {
      open: dialog.open,
      marker: dialog.marker,
      onCancel: dialog.edit ? handleClose : handleCancel,
      onSubmit: dialog.edit ? handleUpdate : handleCreate
    }
  };
}

function initPopup() {
  return {
    enabled: true,
    open: false,
    markers: [],
    virtualRef: createRef()
  };
}

function initDialog() {
  return {
    open: false,
    edit: false,
    marker: null
  };
}

function createVirtualRef(ref, element) {
  const virtualRef = createRef();
  virtualRef.current = {
    getBoundingClientRect: () => getVirtualRect(ref.current, element)
  };

  return virtualRef;
}

function getVirtualRect(chart, element) {
  const { canvas, currentDevicePixelRatio } = chart;
  const rect = canvas.getBoundingClientRect();
  const ratio = (chart.width / canvas.width) * currentDevicePixelRatio;

  return new DOMRect(
    rect.x + element.x * ratio,
    rect.y + element.y * ratio,
    element.width * ratio,
    element.height * ratio
  );
}

export { useDeviceChartMarker };
